import configData from "../config.json";
import axios from "axios";
import signalDispatcherInstance from "../utils/signalDispatcher";

interface UserEvent {
    uuid: string;
    hotel: string | null;
    order_id: string | null;
    cart: object | null;
    event_type: string;
    event_name: string;
    page_url: string;
    previous_uuid?: string | null;
    data?: any;
}

const originalFetch = window.fetch.bind(window);

class UserEventService {
    private lastKnownUuid: string | null = null;
    private initialized: boolean = false;

    private getBaseData(): Omit<UserEvent, 'event_type' | 'event_name' | 'data' | 'previous_uuid'> {
        const uuid = localStorage.getItem('uuid') || '';
        const hotel = localStorage.getItem('hotel') || null;
        const order_id = localStorage.getItem('orderID') || null;
        const cartStr = localStorage.getItem('cart');
        const cart = cartStr ? JSON.parse(cartStr) : null;

        return {
            uuid,
            hotel,
            order_id,
            cart,
            page_url: window.location.href
        };
    }

    private sendUuidChangeEvent(oldUuid: string, newUuid: string): void {
        const event: UserEvent = {
            uuid: newUuid,
            previous_uuid: oldUuid,
            hotel: localStorage.getItem('hotel') || null,
            order_id: localStorage.getItem('orderID') || null,
            cart: null,
            event_type: 'uuid_change',
            event_name: 'UUID változás',
            page_url: window.location.href
        };
        this.postEvent(event);
    }

    private initLocalStorageInterceptor(): void {
        const originalSetItem = localStorage.setItem.bind(localStorage);
        const self = this;

        localStorage.setItem = function (key: string, value: string) {
            if (key === 'uuid') {
                const oldUuid = self.lastKnownUuid;
                originalSetItem(key, value);
                if (oldUuid && value && oldUuid !== value) {
                    self.sendUuidChangeEvent(oldUuid, value);
                }
                self.lastKnownUuid = value || null;
            } else {
                originalSetItem(key, value);
            }
        };
    }

    sendEvent(eventType: string, eventName: string, data?: any): void {
        const base = this.getBaseData();
        const event: UserEvent = {
            ...base,
            event_type: eventType,
            event_name: eventName,
            data
        };
        this.postEvent(event);
    }

    private postEvent(event: UserEvent): void {
        originalFetch(configData.API + '/userEvent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(event)
        }).catch(err => {
            console.error('UserEventService: failed to send event', err);
        });
    }

    private initClickListener(): void {
        document.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const interactiveElement = target.closest('button, a, input, select, textarea, [role="button"]');
            if (!interactiveElement) return;

            const el = interactiveElement as HTMLElement;
            this.sendEvent('click', 'element_click', {
                tagName: el.tagName,
                id: el.id || null,
                className: el.className || null,
                innerText: el.innerText?.substring(0, 200) || null,
                href: (el as HTMLAnchorElement).href || null
            });
        });
    }

    private initFetchInterceptor(): void {
        const self = this;
        window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
            const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;

            if (url.includes('/userEvent') || url.includes('/frontendLabels')) {
                return originalFetch(input, init);
            }

            const method = init?.method || 'GET';
            let requestBody: any = null;
            if (init?.body) {
                try {
                    requestBody = JSON.parse(init.body as string);
                } catch {
                    requestBody = init.body;
                }
            }

            self.sendEvent('ajax_request', `${method} ${url}`, {
                method,
                url,
                body: requestBody
            });

            try {
                const response = await originalFetch(input, init);
                const cloned = response.clone();
                cloned.json().then(responseBody => {
                    self.sendEvent('ajax_response', `${method} ${url}`, {
                        method,
                        url,
                        status: response.status,
                        body: responseBody
                    });
                }).catch(() => {
                    self.sendEvent('ajax_response', `${method} ${url}`, {
                        method,
                        url,
                        status: response.status,
                        body: null
                    });
                });
                return response;
            } catch (error) {
                self.sendEvent('ajax_response', `${method} ${url}`, {
                    method,
                    url,
                    status: 0,
                    error: String(error)
                });
                throw error;
            }
        };
    }

    private initAxiosInterceptor(): void {
        axios.interceptors.request.use((config: any) => {
            const url = config.url || '';
            if (url.includes('/userEvent') || url.includes('/frontendLabels')) {
                return config;
            }
            this.sendEvent('ajax_request', `${config.method?.toUpperCase()} ${url}`, {
                method: config.method?.toUpperCase(),
                url,
                body: config.data || null
            });
            return config;
        });

        axios.interceptors.response.use(
            (response: any) => {
                const url = response.config.url || '';
                if (url.includes('/userEvent') || url.includes('/frontendLabels')) {
                    return response;
                }
                this.sendEvent('ajax_response', `${response.config.method?.toUpperCase()} ${url}`, {
                    method: response.config.method?.toUpperCase(),
                    url,
                    status: response.status,
                    body: response.data
                });
                return response;
            },
            (error: any) => {
                const url = error.config?.url || '';
                if (!url.includes('/userEvent') && !url.includes('/frontendLabels')) {
                    this.sendEvent('ajax_response', `${error.config?.method?.toUpperCase()} ${url}`, {
                        method: error.config?.method?.toUpperCase(),
                        url,
                        status: error.response?.status || 0,
                        error: error.message
                    });
                }
                return Promise.reject(error);
            }
        );
    }

    private initSignalListeners(): void {
        signalDispatcherInstance.on('addToCart', (cart: any) => {
            this.sendEvent('action', 'cart_add', { cart });
        });
        signalDispatcherInstance.on('resetCart', () => {
            this.sendEvent('action', 'cart_clear');
        });
    }

    init(): void {
        if (this.initialized) return;
        this.initialized = true;
        this.lastKnownUuid = localStorage.getItem('uuid');
        this.initLocalStorageInterceptor();
        this.initClickListener();
        this.initFetchInterceptor();
        this.initAxiosInterceptor();
        this.initSignalListeners();
    }
}

const userEventService = new UserEventService();
export default userEventService;
